import React from 'react';
import { COLORS, FONT_SIZE, RADII, SPACE } from '../../theme';
import { bootSearchParams } from '../../../../config/boot/bootUrl';

/**
 * EditorReturnPill — "← Back to Editor" affordance on the editor's
 * test-play surface (DJ 2026-06-11: "our editor test map should have a way
 * of then going back to the editor").
 *
 * The editor's Test button navigates to a plain play surface with
 * ?map=dev-map&fromEditor=1. This pill renders only when that marker is
 * present and links back to /editor?resume=dev-map — EditorSystem's
 * loadInitialLevel honors the resume param above the route-coerced
 * ?map=empty-canvas, so the editor reopens on the exact level being tested.
 */
export const EditorReturnPill: React.FC = () => {
  const fromEditor = React.useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      return bootSearchParams().get('fromEditor') === '1';
    } catch {
      return false;
    }
  }, []);

  if (!fromEditor) return null;

  const backToEditor = () => {
    const sp = new URLSearchParams();
    sp.set('resume', 'dev-map');
    window.location.href = `${window.location.origin}/editor?${sp.toString()}`;
  };

  return (
    <button
      type="button"
      data-testid="editor-return-pill"
      onClick={backToEditor}
      title="Return to the level editor with this map loaded"
      style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.sm,
        padding: `${SPACE.xs}px ${SPACE.md}px`,
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: RADII.pill,
        color: COLORS.textPrimary,
        fontSize: FONT_SIZE.sm,
        cursor: 'pointer',
        pointerEvents: 'auto',
      }}
    >
      🛠 ← Back to Editor
    </button>
  );
};
